import { priceLine, type PromoView } from './promo';
import type { CartLine } from './types';

/**
 * Entfernen mit „Rückgängig“ (K1): reine Funktionen auf dem Warenkorb. Es ist kein Geld im Spiel, solange nichts kassiert
 * wurde; der Warenkorb ist nur Zustand. Die Oberfläche hält höchstens **einen** Rückgängig-Platz (ein neues Entfernen
 * ersetzt das vorige endgültig).
 */

export interface RemovedLine {
  line: CartLine;
  /** Platz im Warenkorb vor dem Entfernen. */
  index: number;
}

export interface UndoEntry {
  removed: RemovedLine[];
}

/** Nimmt eine Zeile heraus. `null`, wenn der Code nicht im Warenkorb liegt. */
export function removeLine(
  cart: readonly CartLine[],
  code: string,
): { cart: CartLine[]; undo: UndoEntry } | null {
  const index = cart.findIndex((line) => line.code === code);
  const line = cart[index];
  if (line === undefined) return null;
  return {
    cart: cart.filter((_, position) => position !== index),
    undo: { removed: [{ line, index }] },
  };
}

/** Leert den Warenkorb. `null`, wenn er schon leer ist. */
export function removeAllLines(
  cart: readonly CartLine[],
): { cart: CartLine[]; undo: UndoEntry } | null {
  if (cart.length === 0) return null;
  return { cart: [], undo: { removed: cart.map((line, index) => ({ line, index })) } };
}

/** Liegt die Zeile (über Code oder Artikel) schon im Warenkorb? */
function present(cart: readonly CartLine[], line: CartLine): boolean {
  return cart.some(
    (other) => other.code === line.code || (line.itemId !== null && other.itemId === line.itemId),
  );
}

/**
 * Legt die entfernten Zeilen an ihren alten Platz zurück. Zeilen, die inzwischen wieder im Warenkorb liegen (erneut
 * gescannt), werden übersprungen: nie doppelt. Der Preis wird **immer** neu aus dem Listenpreis gebildet (die Aktion kann
 * inzwischen gestartet oder beendet worden sein, X7), nie aus dem gemerkten Preis übernommen.
 */
export function restoreLines(
  cart: readonly CartLine[],
  undo: UndoEntry,
  active: PromoView | null,
): { cart: CartLine[]; restored: number } {
  const next = [...cart];
  let restored = 0;
  for (const { line, index } of [...undo.removed].sort((a, b) => a.index - b.index)) {
    if (present(next, line)) continue;
    next.splice(Math.min(index, next.length), 0, priceLine(line, active));
    restored += 1;
  }
  return { cart: next, restored };
}

/** Text der Leiste: „Mütze“ entfernt / 3 Artikel entfernt. */
export function undoText(undo: UndoEntry): string {
  const [only] = undo.removed;
  if (undo.removed.length === 1 && only !== undefined) return `„${only.line.title}“ entfernt`;
  return `${String(undo.removed.length)} Artikel entfernt`;
}

/** So lange nach der letzten Sichtung gilt ein entfernter Code für die Kamera noch als „liegt im Bild“. */
export const REMOVED_GUARD_MS = 2500;

/**
 * Sperre für den Dauerscan nach dem Entfernen: Das Etikett ist meist noch im Kamerabild und würde sofort wieder
 * aufgenommen. Jede Sichtung innerhalb von `REMOVED_GUARD_MS` verlängert die Sperre (das Etikett liegt noch im Bild);
 * erst nach einer Pause ohne Sichtung geht ein bewusster erneuter Scan wieder durch. `guards` merkt je Code die letzte
 * Sichtung (bzw. den Zeitpunkt des Entfernens). `true` = still übergehen.
 */
export function removedGuard(guards: Map<string, number>, code: string, nowMs: number): boolean {
  const last = guards.get(code);
  if (last === undefined) return false;
  if (nowMs - last >= REMOVED_GUARD_MS) {
    guards.delete(code);
    return false;
  }
  guards.set(code, nowMs);
  return true;
}
